import { ArrowLeft } from 'lucide-react';
import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/Button';
import { PasswordField } from '@/shared/components/PasswordField';
import {
  completeInvite,
  resetPasswordWithCode,
  type CodeFlowFailure,
} from '@/app/auth/services/code-flow.service';
import { env } from '@/shared/lib/env';
import { useLogin } from '../useLogin';

type SetPasswordMode = 'invite' | 'reset';

interface SetPasswordStepProps {
  email: string;
  code: string;
  mode: SetPasswordMode;
  onBack: () => void;
}

export const SetPasswordStep = ({ email, code, mode, onBack }: SetPasswordStepProps) => {
  const { t } = useTranslation('app');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [failure, setFailure] = useState<CodeFlowFailure | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const loginMutation = useLogin();

  const passwordsMatch = password.length >= env.VITE_PASSWORD_MIN && password === confirm;

  const handleSubmit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!passwordsMatch) {
      setFormError(
        password.length < env.VITE_PASSWORD_MIN
          ? t('login.passwordTooShort', { min: env.VITE_PASSWORD_MIN })
          : t('login.passwordMismatch'),
      );
      return;
    }
    setFormError(null);
    setFailure(null);
    setIsPending(true);
    void (async () => {
      const result =
        mode === 'invite'
          ? await completeInvite({ email, code, password })
          : await resetPasswordWithCode({ email, code, newPassword: password });
      setIsPending(false);
      if (result.ok) {
        loginMutation.mutate({ email, password });
        return;
      }
      setFailure(result.failure);
    })();
  };

  return (
    <form className="grid gap-1 max-w-md mx-auto" onSubmit={handleSubmit}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-ink-subtle">{email}</span>
        <button
          className="inline-flex items-center gap-1 text-sm text-ink-subtle"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft aria-hidden size={14} />
          {t('login.back')}
        </button>
      </div>

      <p className="mt-4 text-sm text-ink-subtle">
        {mode === 'invite' ? t('login.choosePasswordInvite') : t('login.choosePasswordReset')}
      </p>

      <PasswordField
        autoComplete="new-password"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- password entry is the only action on this step
        autoFocus
        className="mt-2"
        label={t('login.newPassword')}
        onChange={setPassword}
        value={password}
      />

      <PasswordField
        autoComplete="new-password"
        className="mt-2"
        label={t('login.confirmPassword')}
        onChange={setConfirm}
        value={confirm}
      />

      <Button className="mt-6" color="primary" disabled={isPending} fullWidth type="submit">
        {isPending ? t('login.settingPassword') : t('login.setPassword')}
      </Button>

      {formError ? <p className="notice danger">{formError}</p> : null}
      {failure === 'invalid_code' ? (
        <p className="notice danger">{t('login.invalidCode')}</p>
      ) : null}
      {failure === 'locked' ? <p className="notice danger">{t('login.codeLocked')}</p> : null}
      {failure === 'error' ? <p className="notice danger">{t('login.setCodeFailed')}</p> : null}
      {loginMutation.error ? (
        <p className="notice danger">{t('login.passwordSetLoginFailed')}</p>
      ) : null}
    </form>
  );
};
